import {
  APP_INITIALIZER,
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  importProvidersFrom,
  isDevMode,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  TitleStrategy,
  withRouterConfig,
} from '@angular/router';
import { routes } from './app.routes';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { HttpLoaderFactory } from './translation/factories/translate-http-loader';
import { LocaleService } from './translation/services/locale.service';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { PageTitleStrategy } from './core/strategies/page-title-strategy';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { reducers } from './core/store/root.reducer';
import { RuleEffects } from './features/rules/state/rule.effects';
import { RULE_STATE_SERVICE_INJECTION_TOKEN } from './core/services/api/rule-state-service.interface';
import { RuleStateServiceImpl } from './core/services/impl/rule-state.service';
import { DeviceEffects } from './features/devices/state/device.effects';
import { DEVICE_STATE_SERVICE_INJECTION_TOKEN } from './core/services/api/device-state-service.interface';
import { DeviceStateServiceImpl } from './core/services/impl/device-state.service';
import { CISTERN_STATE_SERVICE_INJECTION_TOKEN } from './core/services/api/cistern-state-service.interface';
import { CisternStateServiceImpl } from './core/services/impl/cistern-state.service';
import { CisternEffects } from './features/cisterns/state/cistern.effects';
import { BASE_PATH } from '@birco/rainmesh-api';
import { environment } from '../environments/environment';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AppEffects } from './core/store/app.effects';
import { provideRouterStore } from '@ngrx/router-store';
import {
  provideLdSsoWithOauth2Oidc,
  RoleEnum,
} from '@sso/ld-sso-with-oauth2-oidc';
import { NOTIFICATION_STATE_SERVICE_INJECTION_TOKEN } from './core/services/api/notification-state-service.interface';
import { NotificationStateServiceImpl } from './core/services/impl/notification-state.service';
import { NotificationEffects } from './features/notifications/state/notification.effects';
import { KILL_SWITCH_STATE_SERVICE_INJECTION_TOKEN } from './core/services/api/kill-switch-state-service.interface';
import { KillSwitchStateServiceImpl } from './core/services/impl/kill-switch-state.service';
import { KillSwitchEffects } from './features/dashboard/state/kill-switch.effects';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { DeviceGroupServiceImpl } from './core/services/impl/device-group-state.service';
import { DEVICE_GROUP_STATE_SERVICE_INJECTION_TOKEN } from './core/services/api/device-group-state-service.interface';
import { DeviceGroupEffects } from './features/devices/state/device-groups/device-group.effects';
import { PumpEffects } from './features/pumps/state/pump.effects';
import { ScenarioStateServiceImpl } from './core/services/impl/scenario-state.service';
import { SCENARIO_STATE_SERVICE_INJECTION_TOKEN } from './core/services/api/scenario-state-service.interface';
import { ScenarioEffects } from './features/scenarios/state/scenario.effects';
import { ThemePreferenceService } from './core/services/theme-preference.service';
import { ActionLogStateServiceImpl } from './core/services/impl/action-log-state.service';
import { ACTION_LOG_STATE_SERVICE_INJECTION_TOKEN } from './core/services/api/action-log-state-service.interface';
import { ActionLogEffects } from './features/action-log/state/action-log.effects';
import { provideLottieOptions } from 'ngx-lottie';
import { WEBSOCKET_URL } from './core/tokens/websocket-url.token';

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
    ),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    provideAnimations(),
    provideLottieOptions({
      player: () => import('lottie-web'),
    }),
    importProvidersFrom(MatSnackBarModule),

    {
      provide: BASE_PATH,
      useValue: `${environment.protocolBackend}${environment.backendApiUrl}${environment.contextPathBackend}`,
    },
    {
      provide: WEBSOCKET_URL,
      useValue: `${environment.websocketProtocolBackend}${environment.backendApiUrl}`,
    },

    // Security
    provideLdSsoWithOauth2Oidc(environment.sso, RoleEnum),

    // Strategies
    {
      provide: TitleStrategy,
      useClass: PageTitleStrategy,
    },

    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: {
        disabled: true,
        animation: {
          enterDuration: 0,
          exitDuration: 0,
        },
      },
    },
    {
      provide: LOCALE_ID,
      useFactory: (localeService: LocaleService) => {
        return localeService.userLanguage;
      },
      deps: [LocaleService],
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'EUR',
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [ThemePreferenceService],
      useFactory: () => () => {},
    },

    // Redux
    provideStore(reducers),
    provideRouterStore(),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    provideEffects([
      AppEffects,
      RuleEffects,
      DeviceEffects,
      CisternEffects,
      NotificationEffects,
      ActionLogEffects,
      KillSwitchEffects,
      DeviceGroupEffects,
      ScenarioEffects,
      PumpEffects,
    ]),

    // Services
    {
      provide: RULE_STATE_SERVICE_INJECTION_TOKEN,
      useClass: RuleStateServiceImpl,
    },
    {
      provide: DEVICE_STATE_SERVICE_INJECTION_TOKEN,
      useClass: DeviceStateServiceImpl,
    },
    {
      provide: DEVICE_GROUP_STATE_SERVICE_INJECTION_TOKEN,
      useClass: DeviceGroupServiceImpl,
    },
    {
      provide: CISTERN_STATE_SERVICE_INJECTION_TOKEN,
      useClass: CisternStateServiceImpl,
    },
    {
      provide: NOTIFICATION_STATE_SERVICE_INJECTION_TOKEN,
      useClass: NotificationStateServiceImpl,
    },
    {
      provide: KILL_SWITCH_STATE_SERVICE_INJECTION_TOKEN,
      useClass: KillSwitchStateServiceImpl,
    },
    {
      provide: SCENARIO_STATE_SERVICE_INJECTION_TOKEN,
      useClass: ScenarioStateServiceImpl,
    },
    {
      provide: ACTION_LOG_STATE_SERVICE_INJECTION_TOKEN,
      useClass: ActionLogStateServiceImpl,
    },
  ],
};
